import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { centeredBox } from "../van/centered-box";
import { button, div, h1, input, p, pre } from "../van/van-tags";
import { sp2Placeholder } from "./sp2";

export class Sp2AdminPage implements Route {
	async #addTrial(): Promise<void> {
		const req = this.#ctx.api.post(
			`/smartplug2s/${this.#reactive.sp2.Imei}/trial`,
		);
		req.setJsonBody({
			Days: this.#reactive.trialDays,
		});
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
		}
	}

	async #getSmartplug2(imei: string): Promise<void> {
		const req = this.#ctx.api.get(`/smartplug2s/${imei}`);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
		}
	}

	async #impersonateOwner(): Promise<void> {
		const imei = this.#reactive.sp2.Imei;
		const req = this.#ctx.api.get(
			`/admin/impersonate?key=smartplug2&value=${imei}`,
		);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			state.startImpersonating(await res.json());
			this.#ctx.router.jump(`/devices?id=${imei}`);
		}
	}

	async refresh(match: Match): Promise<void> {
		if (!match?.data?.imei) throw "missing path param: imei";
		const imei = match.data.imei;
		await this.#getSmartplug2(imei);
	}

	#ctx: RouteContext;
	#reactive = reactive({
		sp2: sp2Placeholder,
		trialDays: 0,
	});

	#trialField: ChildDom = () =>
		div(
			{ class: "field is-grouped" },
			div(
				{ class: "control is-expanded" },
				input({
					class: "input",
					inputMode: "numeric",
					oninput: (e: Event) => {
						if (!(e.target instanceof HTMLInputElement)) return;
						this.#reactive.trialDays = +e.target.value;
					},
					pattern: "[1-9][0-9]*",
					placeholder: "Days",
					type: "text",
				}),
			),
			div(
				{ class: "control" },
				button(
					{
						class: "button is-primary is-fullwidth",
						disabled: () => this.#reactive.trialDays < 1,
						onclick: () => this.#addTrial(),
					},
					"Add Trial",
				),
			),
		);

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{
				size: "narrow",
			},
			h1({ class: "title is-4" }, "SmartPlug2 Admin"),
			p(
				{ class: "subtitle is-6" },
				() => `IMEI ${this.#reactive.sp2.Imei}`,
			),
			div(
				{ class: "buttons" },
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/factory-reset"),
						// onclick: () => this.#factoryReset(),
					},
					"Factory Reset",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#ctx.router.down("/fota"),
					},
					"FOTA",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () => this.#impersonateOwner(),
					},
					"Impersonate Owner",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () =>
							this.#ctx.router.jump(
								`/factory/smartplug2/${this.#reactive.sp2.Imei}/commands`,
							),
					},
					"Send Command",
				),
			),
			this.#trialField,
			pre({ class: "is-tiny-mobile" }, () =>
				JSON.stringify(this.#reactive.sp2, null, 2),
			),
		);
	}
}
